import React, { RefObject } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Cancel } from "@xfrontend/xicon";
import DebouncedTextField from "#/modules/common/DebouncedTextField";
import { useLanguage } from "#/i18n/LanguageContext";
import { getPolicyLocale } from "#/i18n/policyTranslations";
import ReactScrollbarsCustom from "#/modules/common/ReactScrollbarsCustom";
import { PolicyItem } from "../types";
import PolicyListItem from "./PolicyListItem";

const LeftPanel = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const LeftPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 1px 2px;
`;

const PolicySearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PolicySearchField = styled(DebouncedTextField)`
  width: 100%;

  input {
    height: 36px;
    padding: 0 36px 0 12px;
    border-radius: 4px;
    border: 1px solid var(--x-purple-500, #494358);
    background: var(--x-purple-600, #3a3646);
    color: var(--x-gray-200, #f6f6f7);
    font-size: 14px;
  }

  input::placeholder {
    color: var(--x-gray-400, #cfd0d3);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PolicyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-300, #cfd0d3);
  line-height: 21px;
`;

type PoliciesLeftPanelProps = {
  defaultPolicies: PolicyItem[];
  customPolicies: PolicyItem[];
  selectedId: string;
  setSelectedId: (id: string) => void;
  query: string;
  setQuery: (value: string) => void;
  searchInputRef: RefObject<HTMLInputElement>;
};

const PoliciesLeftPanel = ({
  defaultPolicies,
  customPolicies,
  selectedId,
  setSelectedId,
  query,
  setQuery,
  searchInputRef,
}: PoliciesLeftPanelProps): JSX.Element => {
  const { language } = useLanguage();
  const { t } = useTranslation("policySpec");
  const getTranslatedName = (policy: PolicyItem) => getPolicyLocale(language, policy.policyName, {
    name: policy.name,
    description: policy.description ?? "",
  }).name;

  return (
    <LeftPanel>
      <ReactScrollbarsCustom>
        <LeftPanelInner>
          <PolicySearchWrapper>
        <PolicySearchField
          ref={searchInputRef}
          placeholder={t("leftPanel.searchPlaceholder")}
          value={query}
          onChange={setQuery}
        />
        <ClearButton
          type="button"
          aria-label={t("aria.clearSearch")}
          onClick={() => {
            setQuery("");
            searchInputRef.current?.blur();
          }}
        >
          <Cancel size={16} color="var(--x-gray-400, #cfd0d3)" />
        </ClearButton>
      </PolicySearchWrapper>
      {defaultPolicies.length > 0 && (
        <>
          <SectionTitle>{t("leftPanel.defaultPolicies")}</SectionTitle>
          <PolicyList>
            {defaultPolicies.map((policy) => (
              <PolicyListItem
                key={policy.id}
                active={policy.id === selectedId}
                onClick={() => setSelectedId(policy.id)}
                onMouseDown={() => setSelectedId(policy.id)}
                name={getTranslatedName(policy)}
                enabled={policy.enabled}
                applied={policy.applied}
                threats={policy.threats}
                checks={policy.checks}
                riskLevel={policy.riskLevel}
              />
            ))}
          </PolicyList>
        </>
      )}
      {customPolicies.length > 0 && (
        <>
          <SectionTitle>{t("leftPanel.customPolicies")}</SectionTitle>
          <PolicyList>
            {customPolicies.map((policy) => (
              <PolicyListItem
                key={policy.id}
                active={policy.id === selectedId}
                onClick={() => setSelectedId(policy.id)}
                onMouseDown={() => setSelectedId(policy.id)}
                name={getTranslatedName(policy)}
                enabled={policy.enabled}
                applied={policy.applied}
                threats={policy.threats}
                checks={policy.checks}
                riskLevel={policy.riskLevel}
              />
            ))}
          </PolicyList>
        </>
      )}
        </LeftPanelInner>
      </ReactScrollbarsCustom>
    </LeftPanel>
  );
};

export default PoliciesLeftPanel;
